import { useEffect, useMemo, useState } from "react";

import PropTypes from "prop-types";

import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";

import MKBox from "components/MKBox";
import MKButton from "components/MKButton";
import MKTypography from "components/MKTypography";

import { company, getImageProperties } from "studio-edifique/siteContent";
import ResponsiveImage from "studio-edifique/components/ResponsiveImage";

function ProjectGalleryDialog({ project, open, onClose }) {
  const [activeImageIndex, setActiveImageIndex] = useState(0);

  const gallery = project?.gallery || [];
  const activeImage = gallery[activeImageIndex] || {
    src: project?.cover,
    alt: project?.title,
  };

  const projectTags = useMemo(() => project?.highlights || [], [project]);

  useEffect(() => {
    setActiveImageIndex(0);
  }, [project]);

  const handlePrevious = () => {
    if (!gallery.length) {
      return;
    }

    setActiveImageIndex((previousIndex) =>
      previousIndex === 0 ? gallery.length - 1 : previousIndex - 1
    );
  };

  const handleNext = () => {
    if (!gallery.length) {
      return;
    }

    setActiveImageIndex((previousIndex) =>
      previousIndex === gallery.length - 1 ? 0 : previousIndex + 1
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <MKBox display="flex" justifyContent="space-between" alignItems="center">
          <MKBox>
            <MKTypography variant="h4">{project?.title}</MKTypography>
            <MKTypography variant="button" color="text" textTransform="uppercase">
              {project?.type}
            </MKTypography>
          </MKBox>
          <IconButton onClick={onClose}>
            <Icon>close</Icon>
          </IconButton>
        </MKBox>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={4}>
          <Grid item xs={12} lg={7}>
            <MKBox
              borderRadius="xl"
              overflow="hidden"
              position="relative"
              sx={{
                backgroundColor: ({ palette: { grey } }) => grey[100],
              }}
            >
              {activeImage?.src && (
                <ResponsiveImage
                  src={activeImage.src}
                  alt={activeImage.alt}
                  eager
                  sizes="(max-width: 900px) 100vw, 60vw"
                  style={{
                    width: "100%",
                    height: "auto",
                    objectFit: "cover",
                    maxHeight: "70vh",
                  }}
                />
              )}
              {gallery.length > 1 && (
                <>
                  <IconButton
                    aria-label="Imagem anterior"
                    onClick={handlePrevious}
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: 16,
                      transform: "translateY(-50%)",
                      bgcolor: "rgba(255,255,255,0.85)",
                    }}
                  >
                    <Icon>chevron_left</Icon>
                  </IconButton>
                  <IconButton
                    aria-label="Próxima imagem"
                    onClick={handleNext}
                    sx={{
                      position: "absolute",
                      top: "50%",
                      right: 16,
                      transform: "translateY(-50%)",
                      bgcolor: "rgba(255,255,255,0.85)",
                    }}
                  >
                    <Icon>chevron_right</Icon>
                  </IconButton>
                </>
              )}
            </MKBox>
            {gallery.length > 1 && (
              <Stack direction="row" spacing={1.5} mt={2} sx={{ overflowX: "auto", pb: 1 }}>
                {gallery.map((image, index) => (
                  <MKBox
                    key={image.src}
                    component="button"
                    type="button"
                    onClick={() => setActiveImageIndex(index)}
                    p={0}
                    border="none"
                    bgColor="transparent"
                    sx={{
                      cursor: "pointer",
                      opacity: activeImageIndex === index ? 1 : 0.6,
                    }}
                  >
                    <MKBox
                      component="img"
                      src={image.src}
                      srcSet={getImageProperties(image.src).srcSet}
                      sizes="96px"
                      alt={image.alt}
                      width="6rem"
                      height="6rem"
                      loading="lazy"
                      decoding="async"
                      borderRadius="lg"
                      sx={{ objectFit: "cover" }}
                    />
                  </MKBox>
                ))}
              </Stack>
            )}
          </Grid>
          <Grid item xs={12} lg={5}>
            <MKTypography variant="button" textTransform="uppercase" color="info">
              Studio Edifique
            </MKTypography>
            <MKTypography variant="h3" mt={1} mb={1.5}>
              {project?.title}
            </MKTypography>
            <MKTypography variant="body2" color="text" mb={3}>
              {project?.description}
            </MKTypography>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" mb={3}>
              {projectTags.map((item) => (
                <Chip key={item} label={item} color="info" variant="outlined" />
              ))}
            </Stack>
            <Divider sx={{ my: 3 }} />
            <MKBox mb={2}>
              <MKTypography variant="button" textTransform="uppercase" color="text">
                Escopo
              </MKTypography>
              <MKTypography variant="body2" mt={0.5}>
                {project?.scope}
              </MKTypography>
            </MKBox>
            <MKBox mb={3}>
              <MKTypography variant="button" textTransform="uppercase" color="text">
                Foco do projeto
              </MKTypography>
              <MKTypography variant="body2" mt={0.5}>
                {project?.focus}
              </MKTypography>
            </MKBox>
            <MKBox
              p={2.5}
              borderRadius="xl"
              sx={{
                background: ({ palette: { grey } }) => grey[100],
              }}
            >
              <MKTypography variant="h6" mb={1}>
                Quer um projeto com essa mesma clareza?
              </MKTypography>
              <MKTypography variant="body2" color="text" mb={2}>
                Fale com a equipe da {company.brandName} para entender escopo, processo e formato de
                atendimento.
              </MKTypography>
              <MKBox display="flex" flexWrap="wrap" gap={1.5}>
                <MKButton
                  component="a"
                  href={company.whatsappUrl}
                  target="_blank"
                  rel="noreferrer"
                  variant="gradient"
                  color="info"
                >
                  Falar no WhatsApp
                </MKButton>
                <MKButton
                  component="a"
                  href={company.instagramUrl}
                  target="_blank"
                  rel="noreferrer"
                  variant="outlined"
                  color="dark"
                >
                  Ver Instagram
                </MKButton>
              </MKBox>
            </MKBox>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
}

ProjectGalleryDialog.defaultProps = {
  project: null,
};

ProjectGalleryDialog.propTypes = {
  project: PropTypes.shape({
    title: PropTypes.string,
    type: PropTypes.string,
    description: PropTypes.string,
    cover: PropTypes.string,
    scope: PropTypes.string,
    focus: PropTypes.string,
    highlights: PropTypes.arrayOf(PropTypes.string),
    gallery: PropTypes.arrayOf(
      PropTypes.shape({
        src: PropTypes.string,
        alt: PropTypes.string,
      })
    ),
  }),
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default ProjectGalleryDialog;
